"use client";

import { FC, useEffect, useState } from "react";
import dayjs from "dayjs";
import Container from "apps/website/components/layout/Container/Container";
import Section from "apps/website/components/layout/Section/Section";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import {
  getSectionSize,
} from "apps/website/components/layout/Section/Section.utils";
import TextTitle from "apps/website/components/base/Text/TextTitle/TextTitle";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import SurveyCardCarousel, {
} from "apps/website/components/feature/Carousel/SurveyCardCarousel/SurveyCardCarousel";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import {
  ISurveyDetail,
  SURVEYS,
  TSurvey,
} from "libs/forms/src/lib/constants/surveys.constants";
import {
  ESurveyState,
} from "apps/website/components/feature/Card/SurveyCard/SurveyCard";
import { LIST_FORMATTER } from "apps/website/utils/misc/arrays";
import { useAPI } from "apps/website/hooks/useAPI";
import { useAuth } from "@auth/client-sdk-react";

export interface ISectionSurveyCardCarousel {
  title?: string;
  subtitle?: string;
  theme?: Theme;
}

const getSurveyState = (survey: ISurveyDetail, completedSurveys: TSurvey[] | undefined): ESurveyState => {
  if (survey.starts && dayjs(survey.starts).isAfter(dayjs())) return ESurveyState.COMING_SOON;
  if (!survey.blockedBy?.length) return ESurveyState.UNLOCKED;
  if (!completedSurveys) return ESurveyState.LOCKED;
  if (!survey.blockedBy?.length || survey.blockedBy.every(
    (key) => completedSurveys.includes(key),
  )) return ESurveyState.UNLOCKED;
  return ESurveyState.LOCKED;
};

const getBlockedByString = (survey: ISurveyDetail): string => LIST_FORMATTER.format(SURVEYS.filter(
  ({ key }) => survey.blockedBy?.includes(key),
).map(({ title }) => title));

const SectionSurveyCardCarousel: FC<ISectionSurveyCardCarousel> = ({ title, subtitle, theme = "dark" }) => {

  const [ completedSurveys, setCompletedSurveys ] = useState<TSurvey[]>();

  const api = useAPI();
  const { id, old, accessToken, loggedIn } = useAuth();

  useEffect(() => {
    const effectFetchAnswers = async () => {
      if (accessToken) {
        try {
          const { data } = await api.SurveyAnswers.getCompleteSurveys();
          setCompletedSurveys(data);
        } catch (error) {
          console.error(error);
        }
      }
    };

    void effectFetchAnswers();

  }, [ id, old, accessToken ]);

  return (
    <Section component="SectionSurveyCardCarousel" theme={ theme } className={ themeRootClassMap[theme] } size={ getSectionSize(theme) }>
      <Container>
        <>
          { title && (
            <>
              <TextTitle size={ 1 } align="center" color="secondary">{ title }</TextTitle>
              <Spacer size="lg" />
            </>
          ) }
          { subtitle && (
            <>
              <TextSubtitle size={ 1 } align="center" color="secondary">{ subtitle }</TextSubtitle>
              <Spacer size="xl" />
            </>
          ) }
          <SurveyCardCarousel items={
            SURVEYS.map((survey) => ({
              ...survey,
              blockedByNote: getBlockedByString(survey),
              state: getSurveyState(survey, completedSurveys),
              linkText: loggedIn ? "Take part" : "Login to take part",
              link: loggedIn ? survey.link : `/login?postLogin=${survey.link}`,
            }))
          } />
        </>
      </Container>
    </Section>
  );
};

export default SectionSurveyCardCarousel;
